import * as Sentry from '@sentry/vue'

import deepMergeObjects from '@/lib/deepMerge'

import { OVERRIDE_NAMESPACES, PATH_REGEX } from './constants'

export const translationFiles = import.meta.glob('../locales/**/*.json', {
  eager: true,
  import: 'default',
})

/**
 * @returns {{ messages: Object, allNamespaces: Set<string>, availableLocales: string[], mergedMessages: Object }}
 */
export function buildTranslationCatalog() {
  const messages = {}
  const allNamespaces = new Set()

  Object.entries(translationFiles).forEach(([path, translation]) => {
    const match = path.match(PATH_REGEX)
    if (!match) {
      return
    }

    const [, lang, namespace] = match

    if (!messages[lang]) {
      messages[lang] = {}
    }

    allNamespaces.add(namespace)

    if (OVERRIDE_NAMESPACES.includes(namespace)) {
      return
    }

    messages[lang][namespace] = translation
  })

  const availableLocales = Object.keys(messages)
  const mergedMessages = {}

  Object.entries(messages).forEach(([lang, namespaceObjects]) => {
    if (!mergedMessages[lang]) {
      mergedMessages[lang] = {}
    }

    Object.entries(namespaceObjects).forEach(([, namespaceTranslation]) => {
      mergedMessages[lang] = deepMergeObjects(mergedMessages[lang], namespaceTranslation)
    })
  })

  return { messages, allNamespaces, availableLocales, mergedMessages }
}

/**
 * @param {string} lang
 * @param {string} namespace
 * @returns {Object|null}
 */
export function getLocalTranslations(lang, namespace) {
  const path = `../locales/${lang}/${namespace}.json`
  if (translationFiles[path]) {
    return translationFiles[path]
  }
  return null
}

/**
 * @param {string} lang
 * @returns {Object}
 */
export function getFallbackLocalTranslations(lang) {
  const localTranslations = {}

  Object.entries(translationFiles).forEach(([path, translation]) => {
    const match = path.match(PATH_REGEX)
    if (match && match[1] === lang) {
      const namespace = match[2]
      localTranslations[namespace] = translation
    }
  })

  Sentry.addBreadcrumb({
    category: 'i18n',
    message: `Using local translations for ${lang}`,
    level: 'info',
  })

  return localTranslations
}
